/* ── What the browser tab says on each Meridian Ops screen ───────────────
 *
 * It said "Meridian Ops" on every one of them (Q-0500, Q-0709). The board,
 * a job's own log, the release candidates and the ceremony were four
 * identical tabs, on the one surface whose whole job is watching several
 * things at once.
 *
 * The words are the screens' own: each is the string that screen passes to
 * `Screen`, which is the `<h1>` a person reads at the top of it, and
 * `ops-tab-titles.spec.ts` walks every address here and fails if the two
 * ever disagree. That is what stops this table drifting away from the
 * screens quietly, which is the standing risk of writing the names twice.
 *
 * The title is composed HERE rather than inside `Screen` itself, although
 * `Screen` is the component that already knows the name. At 900px and up
 * `/jobs/:id` is a split, so `JobsSplit` mounts the list and the detail
 * together and two `Screen`s are on the page at once; the board stream
 * re-renders the list every few seconds, so a title written by whichever
 * one rendered last would flip from "Job" back to "Jobs" while somebody was
 * reading it. A route has one name, and the router is what knows it.
 *
 * A `:id` segment is matched by shape. A job under review is "Review" and
 * not the job's own title, because the job's title is the heading on the
 * page and a tab that changes as the board moves is worse than one that
 * does not.
 */

/** Every address of the app, with the name its own screen renders. */
export const SCREEN_TITLES: Array<{ route: string; title: string }> = [
  { route: '/', title: 'Compose' },
  { route: '/jobs', title: 'Jobs' },
  { route: '/jobs/:id', title: 'Job' },
  { route: '/review/:id', title: 'Review' },
  { route: '/releases', title: 'Releases' },
  { route: '/releases/:runId', title: 'Candidate' },
  { route: '/releases/:runId/promote', title: 'The promotion ceremony' },
  { route: '/blocks', title: 'Blocks' },
  { route: '/notifications', title: 'Notifications' },
  { route: '/capacity', title: 'Capacity' },
  { route: '/history', title: 'History' },
  { route: '/settings', title: 'Settings' },
  { route: '/more', title: 'More' },
];

/* Segment by segment, so `/releases/:runId` and `/releases/:runId/promote`
   cannot be read as each other whichever order they are declared in. */
function matches(route: string, pathname: string): boolean {
  const want = route.split('/');
  const got = pathname.split('/');
  if (want.length !== got.length) return false;
  return want.every((part, i) => (part.startsWith(':') ? got[i].length > 0 : part === got[i]));
}

/** The screen's own name, or null where this address has none. */
export function screenTitleFor(pathname: string): string | null {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return SCREEN_TITLES.find((s) => matches(s.route, clean))?.title ?? null;
}

/** The application's own name, which every tab carries after the screen's. */
export const APP_TITLE = 'Meridian Ops';
